import { AlertTriangle, CheckCircle2, Pencil, ToggleLeft, ToggleRight, Trash2, X } from "lucide-react";

import { apiErrorMessage } from "../../utils/apiError";
import { describeUsage } from "./registerLogic";

export type NoticeState = { tone: "success" | "error"; text: string } | null;

/** What just happened, or why the server refused it. */
export function Notice({ notice, onDismiss }: { notice: NoticeState; onDismiss: () => void }) {
  if (!notice) return null;
  const error = notice.tone === "error";
  const Icon = error ? AlertTriangle : CheckCircle2;
  return (
    <div
      role={error ? "alert" : "status"}
      className={`flex items-start gap-3 rounded-lg border p-4 text-sm ${error ? "border-red-200 bg-red-50 text-red-800" : "border-green-200 bg-green-50 text-green-800"}`}
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0" />
      <span className="flex-1">{notice.text}</span>
      <button type="button" onClick={onDismiss} aria-label="Dismiss" className="rounded p-1 opacity-70 hover:opacity-100">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** A refused delete carries `detail` (the sentence) and `in_use` (the counts);
 * only the sentence is for people. */
export function deleteErrorText(error: unknown) {
  const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  return detail || apiErrorMessage(error, "It could not be deleted.");
}

/**
 * Delete / Close / Deactivate-Activate / Edit for a record's details panel.
 * Delete shows only once the server has said nothing depends on the record
 * (`inUse` is empty); while it is still being asked, the button waits.
 */
export function RecordFooter({ canWrite, isActive, inUse, busy, onDelete, onToggle, onEdit, onClose }: {
  canWrite: boolean;
  isActive: boolean;
  inUse?: Record<string, number>;
  busy: boolean;
  onDelete: () => void;
  onToggle: () => void;
  onEdit: () => void;
  onClose: () => void;
}) {
  return (
    <>
      {canWrite && inUse && !describeUsage(inUse) && (
        <button type="button" disabled={busy} onClick={onDelete} className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
          <Trash2 className="h-4 w-4" /> Delete
        </button>
      )}
      <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
      {canWrite && (
        <>
          <button type="button" disabled={busy} onClick={onToggle} className="btn btn-outline gap-1.5">
            {isActive ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
            {isActive ? "Make inactive" : "Make active"}
          </button>
          <button type="button" className="btn btn-primary gap-1.5" onClick={onEdit}>
            <Pencil className="h-4 w-4" /> Edit
          </button>
        </>
      )}
    </>
  );
}

/** Why Delete is missing, when something still depends on the record. */
export function UsageNotice({ inUse, advice }: { inUse?: Record<string, number>; advice: string }) {
  const usage = describeUsage(inUse);
  if (!usage) return null;
  return (
    <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
      In use by {usage}, so it can't be deleted. {advice}
    </p>
  );
}
